import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import { inventoryDetailLabelSx } from './InventoryItemDetails';
import { formatInventoryPriceLabel } from '../utils/inventoryPrice';

export const pricingChipSx = (active) => ({
  border: `1px solid ${active ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
  bgcolor: active ? `color-mix(in srgb, ${DOCUMENT.oxblood} 10%, transparent)` : 'transparent',
  color: active ? DOCUMENT.oxblood : DOCUMENT.ink2,
  borderRadius: 0,
  px: 1.25,
  py: 0.5,
  minHeight: 28,
  height: 28,
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  font: 'inherit',
  lineHeight: 1,
  cursor: 'pointer',
  minWidth: 0,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: active
      ? `color-mix(in srgb, ${DOCUMENT.oxblood} 14%, transparent)`
      : `color-mix(in srgb, ${DOCUMENT.ink} 4%, transparent)`,
    boxShadow: 'none',
  },
});

const ChipGroup = ({ label, options, value, onChange, testIdPrefix }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} role="group" aria-label={label}>
    {options.map((option) => (
      <Button
        key={option.value}
        type="button"
        sx={pricingChipSx(value === option.value)}
        onClick={() => onChange(option.value)}
        data-testid={`${testIdPrefix}-${option.testId}`}
      >
        {option.label}
      </Button>
    ))}
  </Box>
);

/**
 * How a catalog item goes on a trip: charged or complimentary, on the
 * traveler's bill or waiting for them to buy it, and how many days/weeks.
 * `item` drives the price hint (name, price, frequency, currency).
 */
const PartnerTripDeliveryOptions = ({
  item,
  pricingMode = 'charge',
  onPricingModeChange,
  deliveryMode = 'charge',
  onDeliveryModeChange,
  showQuantity = false,
  quantity = '1',
  onQuantityChange,
  testIdPrefix,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Box>
        <Typography sx={inventoryDetailLabelSx}>{t('partnerInventory.pricingMode')}</Typography>
        <ChipGroup
          label={t('partnerInventory.pricingMode')}
          value={pricingMode}
          onChange={(mode) => onPricingModeChange?.(mode)}
          testIdPrefix={testIdPrefix}
          options={[
            { value: 'charge', testId: 'charge', label: t('partnerInventory.chargeGuest') },
            { value: 'free', testId: 'free', label: t('partnerInventory.offerFree') },
          ]}
        />
        {item?.name ? (
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 1 }}>
            {pricingMode === 'free'
              ? t('partnerInventory.offerFreeHint', { name: item.name })
              : t('partnerInventory.chargeGuestHint', {
                name: item.name,
                price: formatInventoryPriceLabel(item, t),
              })}
          </Typography>
        ) : null}
      </Box>

      {onDeliveryModeChange ? (
        <Box>
          <Typography sx={inventoryDetailLabelSx}>{t('partnerAddons.deliveryMode')}</Typography>
          <ChipGroup
            label={t('partnerAddons.deliveryMode')}
            value={deliveryMode}
            onChange={onDeliveryModeChange}
            testIdPrefix={testIdPrefix}
            options={[
              { value: 'charge', testId: 'deliver-charge', label: t('partnerAddons.deliveryCharge') },
              { value: 'offer', testId: 'deliver-offer', label: t('partnerAddons.deliveryOffer') },
            ]}
          />
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 1 }}>
            {deliveryMode === 'offer'
              ? t('partnerAddons.deliveryOfferHint')
              : t('partnerAddons.deliveryChargeHint')}
          </Typography>
        </Box>
      ) : null}

      {showQuantity && pricingMode === 'charge' ? (
        <TextField
          fullWidth
          margin="dense"
          type="number"
          label={t('partnerAddons.quantity')}
          value={quantity}
          onChange={(event) => onQuantityChange?.(event.target.value)}
          variant="standard"
          inputProps={{ min: 1, 'data-testid': `${testIdPrefix}-quantity` }}
        />
      ) : null}
    </>
  );
};

export default PartnerTripDeliveryOptions;
